import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * The browser softphone. It signs in to Plivo with a short-lived token from the CRM and places calls the CRM has
 * already created (the CRM decides which number is dialled; the browser only sends the call id).
 * The Plivo SDK is about 1 MB, so it is loaded only for people who have calling switched on.
 */
export type Phase = "off" | "connecting" | "ready" | "offline" | "incoming" | "dialing" | "ringing" | "live" | "ended";

export interface CallView {
  /** Who is being called, e.g. "Test call to your mobile" or a lead's name */
  label: string;
  sub?: string;
  callId: string;
  /** Number shown to Plivo (the CRM ignores it and dials what it stored) */
  dial: string;
  /** What the call was made from; a test call (or a caller we don't know) has no outcome to record */
  recordType?: "LEAD" | "CONTACT" | "DEAL" | "ACCOUNT" | "TEST";
  /** A customer calling us: the number they rang from, so an unknown caller can be linked to a record */
  inbound?: { fromDisplay: string };
}

/** A customer calling the shared number, as the incoming-call card shows it */
export interface IncomingCall {
  callId: string;
  from_display: string;
  known: boolean;
  label: string | null;
  record_type: string | null;
  record_id: string | null;
  sub: string | null;
  owner_name: string | null;
  /** Why it rings for this person: they own the record, or it is a team / overflow call */
  why: "OWNER" | "TEAM" | "OVERFLOW";
  conflict: boolean;
  other_names: string[];
}

/** A recent call of the rep's that still needs an outcome */
export interface PendingCall {
  id: string;
  related_type: string;
  related_id: string;
  label: string;
  to_display: string;
  call_status: string;
  duration_sec: number | null;
  started_at: string;
}

/** What a call is made to: the CRM looks the number up from the record and checks the rep may call it */
export interface CallTarget {
  type: "LEAD" | "CONTACT" | "DEAL" | "ACCOUNT";
  id: string;
  field?: "mobile" | "phone" | "alternate_phone";
}

/** The rep's status for incoming calls, as the CRM sees it */
export type RepStatus = "AVAILABLE" | "ON_CALL" | "WRAP_UP" | "AWAY" | "OFFLINE";
export interface RepInfo {
  status: RepStatus;
  /** AWAY: when it ends by itself (null = until the rep turns calls back on) */
  awayUntil: string | null;
  wrapUpSec: number;
  allowPause: boolean;
}

interface Softphone {
  phase: Phase;
  /** Why the softphone is off: no calling permission, the company's Plivo setup isn't finished, or calling isn't switched on for this user */
  offReason: "restricted" | "not_set_up" | "not_enabled" | null;
  /** A problem worth showing: microphone blocked, Plivo unreachable, call failed */
  problem: string | null;
  call: CallView | null;
  muted: boolean;
  /** Seconds since the other side answered (0 until then); frozen once the call ends */
  seconds: number;
  recording: boolean;
  canTestCall: boolean;
  hasMobile: boolean;
  /** Someone is calling the shared number and it is ringing here */
  incoming: IncomingCall | null;
  /** A customer is calling right now while this rep is busy (they get voicemail); clears itself after a short while */
  waiting: { label: string; sub: string; number: string } | null;
  /** Goes up whenever the CRM says a status or a call changed (live stream); screens that show them refetch on it */
  liveTick: number;
  /** Goes up when something about the rep's (or their team's) calls changed: a call came in, was missed, handled or ended */
  callsTick: number;
  /** Available / on a call / wrap-up / away: what decides whether a customer's call rings this rep */
  rep: RepInfo;
  /** Pauses incoming calls for some minutes (or until turned back on with null); false turns them back on */
  setAway: (minutes: number | null | false) => Promise<void>;
  /** Seconds of wrap-up left after a connected call (0 when none): no new calls ring meanwhile */
  wrapLeft: number;
  /** The rep is ready for the next call: wrap-up ends early */
  readyNow: () => void;
  /** Missed incoming calls still waiting for a callback (shown on the top-bar badge with the outcomes waiting) */
  missedCallbacks: number;
  /** The call's record changed (an unknown caller was linked): the outcome box and label follow it */
  updateCall: (patch: Partial<CallView>) => void;
  /** Ringtone preferences of this device (the sound itself is set by a Super Admin) */
  ringtone: { url: string; adminVolume: number; on: boolean; volume: number };
  setRingtoneOn: (on: boolean) => void;
  setRingtoneVolume: (v: number) => void;
  testRingtone: () => void;
  answer: () => Promise<void>;
  /** Not me: the call moves on to the next person */
  /** Sends this call to the rep's voicemail (for their own customer) without passing it on */
  decline: () => Promise<void>;
  startTestCall: () => Promise<void>;
  /** Starts a call to a lead, contact, deal or account; rejects with the server's reason when it can't (access, hours, no number) */
  startCall: (target: CallTarget) => Promise<void>;
  /** Recent calls still waiting for an outcome (the CRM lists them; test calls never appear) */
  pending: PendingCall[];
  refreshPending: () => Promise<void>;
  /** Saves the signed-in user's own mobile number (the test call rings it) */
  saveMobile: (mobile: string) => Promise<void>;
  hangup: () => void;
  toggleMute: () => void;
  sendDigit: (d: string) => void;
  dismiss: () => void;
}

const noop = async () => undefined;
const Ctx = createContext<Softphone>({
  phase: "off", offReason: null, problem: null, call: null, muted: false, seconds: 0, recording: false, canTestCall: false, hasMobile: false, incoming: null, liveTick: 0, callsTick: 0, waiting: null, rep: { status: "OFFLINE", awayUntil: null, wrapUpSec: 30, allowPause: true }, setAway: noop, wrapLeft: 0, readyNow: () => undefined, missedCallbacks: 0, updateCall: () => undefined, ringtone: { url: "/sounds/ring-chime.wav", adminVolume: 70, on: true, volume: 100 }, setRingtoneOn: () => undefined, setRingtoneVolume: () => undefined, testRingtone: () => undefined, answer: noop, decline: noop,
  pending: [], refreshPending: noop, startTestCall: noop, startCall: noop, saveMobile: noop, hangup: () => undefined, toggleMute: () => undefined, sendDigit: () => undefined, dismiss: () => undefined,
});
export const useSoftphone = () => useContext(Ctx);

/** Asks for the microphone up front, so a blocked one gives a clear message instead of a silent call */
async function checkMicrophone(): Promise<string | null> {
  if (!navigator.mediaDevices?.getUserMedia) return "This browser can't use a microphone here. Calling needs a secure (https) page in a recent browser.";
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    return null;
  } catch (err: any) {
    if (err?.name === "NotFoundError") return "No microphone found. Plug in a headset and try again.";
    return "The microphone is blocked. Click the lock icon next to the address, allow the microphone for this site, then try again.";
  }
}

const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private window */ } },
};

export function SoftphoneProvider({ children }: { children: React.ReactNode }) {
  const { user, hasPermission } = useAuth();
  const allowed = Boolean(user) && hasPermission("call.make");

  const [phase, setPhase] = useState<Phase>("off");
  const [offReason, setOffReason] = useState<"restricted" | "not_set_up" | "not_enabled" | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [call, setCall] = useState<CallView | null>(null);
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [recording, setRecording] = useState(false);
  const [hasMobile, setHasMobile] = useState(false);
  const [pending, setPending] = useState<PendingCall[]>([]);
  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [missedCallbacks, setMissedCallbacks] = useState(0);
  const [startKey, setStartKey] = useState(0);
  const [liveTick, setLiveTick] = useState(0);
  const [callsTick, setCallsTick] = useState(0);
  const [waiting, setWaiting] = useState<{ label: string; sub: string; number: string } | null>(null);
  const waitingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectWatch = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rep, setRep] = useState<RepInfo>({ status: "OFFLINE", awayUntil: null, wrapUpSec: 30, allowPause: true });
  const [wrapEndsAt, setWrapEndsAt] = useState<number | null>(null);
  const [wrapLeft, setWrapLeft] = useState(0);
  const wrapSecRef = useRef(30);
  const wrapEndsAtRef = useRef<number | null>(null);
  const incomingRef = useRef<IncomingCall | null>(null);
  useEffect(() => { incomingRef.current = incoming; }, [incoming]);
  useEffect(() => { wrapSecRef.current = rep.wrapUpSec; }, [rep.wrapUpSec]);
  useEffect(() => { wrapEndsAtRef.current = wrapEndsAt; }, [wrapEndsAt]);

  // Counts the wrap-up down; a new call (or the time running out) ends it
  useEffect(() => {
    if (!wrapEndsAt) { setWrapLeft(0); return; }
    const tick = () => {
      const left = Math.ceil((wrapEndsAt - Date.now()) / 1000);
      if (left <= 0) { setWrapEndsAt(null); setWrapLeft(0); } else setWrapLeft(left);
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [wrapEndsAt]);
  useEffect(() => {
    if (phase === "dialing" || phase === "incoming") setWrapEndsAt(null);
  }, [phase]);
  const [ring, setRing] = useState({ url: "/sounds/ring-chime.wav", adminVolume: 70, desktopNotify: true });
  const [ringOn, setRingOn] = useState(() => store.get("softphone-ring") !== "off");
  const [ringVolume, setRingVolumeState] = useState(() => Math.min(100, Math.max(0, Number(store.get("softphone-ring-volume") ?? 100))));
  const audio = useRef<HTMLAudioElement | null>(null);

  const sdk = useRef<any>(null);
  const phaseRef = useRef<Phase>("off");
  const answeredAt = useRef<number | null>(null);
  const lastLogin = useRef(0);
  const setPhaseBoth = useCallback((p: Phase) => { phaseRef.current = p; setPhase(p); }, []);

  const refreshPending = useCallback(async () => {
    try {
      setPending(unwrapObject<PendingCall[]>(await api.get("/api/Call/Pending")) || []);
    } catch {
      /* the list is a convenience; the dock works without it */
    }
    // Missed incoming calls waiting for a callback count on the badge too (reps without the Calls page just see 0)
    try {
      setMissedCallbacks(unwrapObject<{ counts: { callback: number } }>(await api.get("/api/Call/Stats")).counts?.callback || 0);
    } catch {
      setMissedCallbacks(0);
    }
  }, []);

  const login = useCallback(async () => {
    // Several events can ask for a new sign-in at once; one every few seconds is plenty
    if (Date.now() - lastLogin.current < 4000) return;
    lastLogin.current = Date.now();
    try {
      const r = unwrapObject<{ token: string | null; username: string; password: string | null }>(await api.get("/api/Call/Token"));
      // Signing in with the login's own password registers the browser, so Plivo can ring it for incoming calls
      if (r.password) sdk.current?.client.login(r.username, r.password);
      else if (r.token) sdk.current?.client.loginWithAccessToken(r.token);
    } catch (err: any) {
      setProblem(err?.message || "Couldn't sign in to calling");
      if (phaseRef.current === "connecting") setPhaseBoth("offline");
    }
  }, [setPhaseBoth]);

  // Switch the softphone on when the user may call and calling is enabled for them
  useEffect(() => {
    if (!allowed) { setOffReason("restricted"); setPhaseBoth("off"); return; }
    let cancelled = false;
    (async () => {
      try {
        const me = unwrapObject<{ available: boolean; enabled: boolean; recording: boolean; has_mobile: boolean; ringtone?: { url: string }; ringtone_volume?: number; desktop_notify?: boolean; wrap_up_sec?: number; allow_pause?: boolean }>(await api.get("/api/Call/Me"));
        if (cancelled) return;
        if (!me.available || !me.enabled) { setOffReason(!me.available ? "not_set_up" : "not_enabled"); return setPhaseBoth("off"); }
        setOffReason(null);
        setRecording(me.recording);
        setHasMobile(me.has_mobile);
        setRep((r) => ({ ...r, wrapUpSec: me.wrap_up_sec ?? 30, allowPause: me.allow_pause ?? true }));
        if (me.ringtone?.url) setRing({ url: me.ringtone.url, adminVolume: me.ringtone_volume ?? 70, desktopNotify: me.desktop_notify ?? true });
        void refreshPending();
        setPhaseBoth("connecting");
        const mod: any = await import("plivo-browser-sdk");
        if (cancelled) return;
        const Plivo = mod.default || mod;
        const p = new Plivo({
          // localStorage "softphone-debug" = "1" turns on the SDK's full log (SIP messages) in the browser console
          debug: (() => { try { return localStorage.getItem("softphone-debug") === "1" ? "ALL" : "ERROR"; } catch { return "ERROR"; } })(),
          // Ask for the microphone when a call starts, not on every page load
          permOnClick: true,
          enableTracking: false,
          closeProtection: true,
          allowMultipleIncomingCalls: false,
          audioConstraints: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        sdk.current = p;
        const c = p.client;
        // The ringtone is the one a Super Admin chose, played by the CRM (see below), not the SDK's own
        try { c.setRingTone(false); } catch { /* older SDK */ }
        c.on("onLogin", () => {
          console.info("[softphone] signed in to Plivo"); if (reconnectWatch.current) { clearTimeout(reconnectWatch.current); reconnectWatch.current = null; } setProblem(null); if (phaseRef.current === "connecting" || phaseRef.current === "offline") setPhaseBoth("ready"); });
        c.on("onLoginFailed", (cause: any) => {
          const text = typeof c.getErrorStringByErrorCodes === "function" ? c.getErrorStringByErrorCodes(cause) : String(cause || "");
          // An expired token is normal: ask for a new one
          if (String(cause) === "10006" || /expired/i.test(String(text))) { lastLogin.current = 0; login(); return; }
          setProblem(`The calling account didn't accept the sign-in${text ? `: ${text}` : ""}`);
          if (phaseRef.current === "connecting" || phaseRef.current === "ready") setPhaseBoth("offline");
        });
        c.on("onLogout", () => { if (phaseRef.current === "ready") setPhaseBoth("offline"); });
        c.on("onConnectionChange", (info: any) => {
          if (info?.state === "connected") {
            login();
            // After a dropped line the SDK may not say "signed in" again: if we are still waiting after a while, sign in afresh
            if (reconnectWatch.current) clearTimeout(reconnectWatch.current);
            reconnectWatch.current = setTimeout(() => {
              if (phaseRef.current !== "connecting") return;
              lastLogin.current = 0;
              login();
              reconnectWatch.current = setTimeout(() => {
                if (phaseRef.current !== "connecting") return;
                setProblem("Couldn't get back on the phone line. Reload the page; if it keeps happening, tell your admin.");
                setPhaseBoth("offline");
              }, 12000);
            }, 12000);
          }
          else if (info?.state === "disconnected" && phaseRef.current === "ready") { setProblem("Lost the connection to the calling account. Reconnecting…"); setPhaseBoth("connecting"); }
        });
        c.on("onWebrtcNotSupported", () => { setProblem("This browser can't make calls. Use a recent Chrome, Edge or Firefox."); setPhaseBoth("offline"); });
        c.on("onMediaPermission", (evt: any) => { if (evt && evt.status && evt.status !== "success") setProblem("The microphone isn't available to the call. Allow it in the browser and try again."); });
        c.on("onIncomingCall", (callerName: any, extra: any, info: any) => {
          console.info("[softphone] incoming call", { callerName, extra, info, phase: phaseRef.current });
          // Only one at a time; a call that arrives while we are busy is left for the next person
          if (phaseRef.current !== "ready") { try { c.reject(); } catch { /* nothing to reject */ } return; }
          const headers = extra || {};
          const key = Object.keys(headers).find((k) => k.toLowerCase() === "x-ph-callid");
          // Sent without dashes (Plivo allows only letters and digits in SIP headers): put them back
          const raw = String((key && headers[key]) || "").replace(/-/g, "");
          const callId = /^[0-9a-f]{32}$/i.test(raw) ? `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20)}` : raw;
          const from = String(callerName || info?.src || "Unknown number");
          const basic: IncomingCall = { callId, from_display: from, known: false, label: null, record_type: null, record_id: null, sub: null, owner_name: null, why: "OVERFLOW", conflict: false, other_names: [] };
          answeredAt.current = null;
          setSeconds(0);
          setMuted(false);
          setProblem(null);
          setIncoming(basic);
          setCall({ label: from, callId, dial: "", inbound: { fromDisplay: from } });
          setPhaseBoth("incoming");
          if (/^[0-9a-f-]{36}$/i.test(callId)) {
            api.get(`/api/Call/Incoming/${callId}`).then((r) => {
              const d = unwrapObject<Omit<IncomingCall, "callId"> & { id: string }>(r);
              if (phaseRef.current !== "incoming") return;
              setIncoming({ ...basic, ...d, callId });
              setCall({ label: d.label || d.from_display, sub: [d.sub, d.from_display].filter(Boolean).join(" · "), callId, dial: "", recordType: (d.record_type as CallView["recordType"]) || undefined, inbound: { fromDisplay: d.from_display } });
            }).catch(() => undefined);
          }
        });
        c.on("onIncomingCallCanceled", (info: any) => {
          console.info("[softphone] incoming call cancelled", info);
          if (phaseRef.current === "incoming") { setIncoming(null); setCall(null); setPhaseBoth("ready"); }
        });
        c.on("onCalling", () => setPhaseBoth("dialing"));
        c.on("onCallRemoteRinging", () => setPhaseBoth("ringing"));
        c.on("onCallAnswered", () => { answeredAt.current = Date.now(); setSeconds(0); setIncoming(null); setPhaseBoth("live"); });
        c.on("onCallTerminated", () => {
          setMuted(false);
          // A connected call is followed by a short wrap-up: the rep saves the outcome and no new calls ring meanwhile
          if (answeredAt.current) setWrapEndsAt((cur) => cur ?? Date.now() + 1000 * (wrapSecRef.current || 0));
          setPhaseBoth("ended");
        });
        c.on("onCallFailed", (cause: any) => { setMuted(false); setProblem(`The call didn't connect${cause ? ` (${String(cause)})` : ""}. If it ended at once and nothing rang, an admin should check the calling setup in Settings → Calling.`); setPhaseBoth("ended"); });
        login();
      } catch (err: any) {
        if (!cancelled) { setProblem(err?.message || "Couldn't start calling"); setPhaseBoth("offline"); }
      }
    })();
    return () => {
      cancelled = true;
      try { sdk.current?.client.logout(); } catch { /* already signed out */ }
      sdk.current = null;
    };
  }, [allowed, login, refreshPending, setPhaseBoth, startKey]);

  // Calling switched on for this person while the tab was already open: it starts by itself within a minute (no reload)
  useEffect(() => {
    if (!allowed || phase !== "off") return;
    const t = setInterval(async () => {
      try {
        const me = unwrapObject<{ available: boolean; enabled: boolean }>(await api.get("/api/Call/Me"));
        if (me.available && me.enabled) setStartKey((k) => k + 1);
      } catch { /* try again next time */ }
    }, 60000);
    return () => clearInterval(t);
  }, [allowed, phase]);

  const applyRep = useCallback((r: any) => {
    if (!r || !r.status) return;
    setRep({ status: r.status, awayUntil: r.away_until ?? null, wrapUpSec: r.wrap_up_sec ?? 30, allowPause: r.allow_pause ?? true });
  }, []);

  // Tell the CRM this softphone is open and whether its rep is on a call, so incoming calls ring only people who can take them
  const ping = useCallback(() => {
    const busy = phaseRef.current === "dialing" || phaseRef.current === "ringing" || phaseRef.current === "live";
    // A hidden tab stays quiet, unless a call is on: the CRM must not think a rep on a call is free
    if (document.visibilityState === "hidden" && !busy) return;
    api.post("/api/Call/Presence", { busy }).then((r) => applyRep(unwrapObject<any>(r))).catch(() => undefined);
  }, [applyRep]);
  const online = !(!allowed || phase === "off" || phase === "connecting" || phase === "offline");
  useEffect(() => {
    if (!online) return;
    ping();
    const t = setInterval(ping, 30000);
    document.addEventListener("visibilitychange", ping);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", ping); };
  }, [online, ping]);
  // A call starting or ending is told to the CRM at once, not at the next heartbeat
  const onCall = phase === "dialing" || phase === "ringing" || phase === "live";
  useEffect(() => { if (online) ping(); }, [onCall]);

  // Live updates: the CRM tells us when a status or a call changed (and we check on a timer if the stream is cut)
  useEffect(() => {
    if (!allowed || phase === "off") return;
    const me = user?.Id;
    let source: EventSource | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let soon: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    const onEvent = (e: { kind?: string; user_id?: string | null; waiting?: { label: string; sub: string; number: string } } | null) => {
      setLiveTick((t) => t + 1);
      if (e?.kind === "waiting" && e.waiting && e.user_id === me) {
        setWaiting({ label: e.waiting.label, sub: e.waiting.sub, number: e.waiting.number });
        if (waitingTimer.current) clearTimeout(waitingTimer.current);
        waitingTimer.current = setTimeout(() => setWaiting(null), 45000);
      }
      if (!e || e.kind === "calls") {
        setCallsTick((t) => t + 1);
        if (soon) clearTimeout(soon);
        soon = setTimeout(() => { void refreshPending(); }, 400);
      }
      if (!e || (e.kind === "status" && (!e.user_id || e.user_id === me))) api.get("/api/Call/MyStatus").then((r) => applyRep(unwrapObject<any>(r))).catch(() => undefined);
    };
    const startPolling = () => { if (!poll) poll = setInterval(() => onEvent(null), 15000); };
    const stopPolling = () => { if (poll) clearInterval(poll); poll = null; };
    const connect = () => {
      if (closed || typeof EventSource === "undefined") return startPolling();
      source = new EventSource("/api/Call/Stream", { withCredentials: true });
      source.addEventListener("open", () => { stopPolling(); onEvent(null); });
      source.addEventListener("update", (ev) => { try { onEvent(JSON.parse((ev as MessageEvent).data)); } catch { onEvent(null); } });
      source.addEventListener("error", () => { source?.close(); startPolling(); retry = setTimeout(connect, 30000); });
    };
    connect();
    return () => { closed = true; source?.close(); stopPolling(); if (retry) clearTimeout(retry); if (soon) clearTimeout(soon); };
  }, [allowed, phase === "off", user?.Id, refreshPending, applyRep]);

  // The heads-up is only for the length of the call it interrupted
  useEffect(() => {
    if (phase !== "live" && phase !== "dialing" && phase !== "ringing" && phase !== "ended") setWaiting(null);
  }, [phase]);

  // The call timer
  useEffect(() => {
    if (phase !== "live") return;
    const t = setInterval(() => { if (answeredAt.current) setSeconds(Math.floor((Date.now() - answeredAt.current) / 1000)); }, 500);
    return () => clearInterval(t);
  }, [phase]);

  const place = useCallback(async (view: CallView) => {
    const mic = await checkMicrophone();
    if (mic) { setProblem(mic); return; }
    if (!sdk.current) { setProblem("Calling isn't ready yet."); return; }
    setProblem(null);
    setCall(view);
    answeredAt.current = null;
    setSeconds(0);
    setMuted(false);
    setPhaseBoth("dialing");
    const ok = sdk.current.client.call(view.dial, { "X-PH-CallId": view.callId });
    if (ok === false) { setProblem("The call couldn't start. Check your connection and try again."); setPhaseBoth("ended"); }
  }, [setPhaseBoth]);

  const answer = useCallback(async () => {
    if (phaseRef.current !== "incoming") return;
    const mic = await checkMicrophone();
    if (mic) { setProblem(mic); return; }
    try { sdk.current?.client.answer(); } catch { setProblem("Couldn't pick up the call."); }
  }, []);
  const decline = useCallback(async () => {
    const id = incomingRef.current?.callId;
    // The CRM hears about the decline first, so it knows this was a decline and not a network failure (it waits a moment at most)
    if (id && /^[0-9a-f-]{36}$/i.test(id)) {
      await Promise.race([api.post(`/api/Call/Incoming/${id}/Decline`).catch(() => undefined), new Promise((r) => setTimeout(r, 1200))]);
    }
    try { sdk.current?.client.reject(); } catch { /* already gone */ }
    setIncoming(null);
    setCall(null);
    setPhaseBoth("ready");
  }, [setPhaseBoth]);

  const setAway = useCallback(async (minutes: number | null | false) => {
    const r = unwrapObject<any>(await api.post("/api/Call/Status", minutes === false ? { status: "AVAILABLE" } : { status: "AWAY", ...(minutes ? { minutes } : {}) }));
    applyRep(r);
  }, [applyRep]);

  const readyNow = useCallback(() => {
    setWrapEndsAt(null);
    setWrapLeft(0);
    api.post("/api/Call/Ready").then((r) => applyRep(unwrapObject<any>(r))).catch(() => undefined);
  }, [applyRep]);

  const startTestCall = useCallback(async () => {
    if (phaseRef.current !== "ready") return;
    try {
      const r = unwrapObject<{ call_id: string; to: string; to_display: string }>(await api.post("/api/Call/TestCall"));
      await place({ label: "Test call to your mobile", sub: r.to_display, callId: r.call_id, dial: r.to, recordType: "TEST" });
    } catch (err: any) {
      setProblem(err?.message || "Couldn't start the test call");
    }
  }, [place]);

  const startCall = useCallback(async (t: CallTarget) => {
    const r = unwrapObject<{ call_id: string; to: string; to_display: string; label: string; sub: string }>(
      await api.post("/api/Call/Start", { related_type: t.type, related_id: t.id, ...(t.field ? { field: t.field } : {}) }),
    );
    await place({ label: r.label, sub: [r.sub, r.to_display].filter(Boolean).join(" · "), callId: r.call_id, dial: r.to, recordType: t.type });
  }, [place]);

  const saveMobile = useCallback(async (mobile: string) => {
    const me = unwrapObject<{ has_mobile: boolean }>(await api.post("/api/Call/MyMobile", { mobile }));
    setHasMobile(me.has_mobile);
  }, []);

  const hangup = useCallback(() => { try { sdk.current?.client.hangup(); } catch { /* no active call */ } }, []);
  const toggleMute = useCallback(() => {
    const c = sdk.current?.client;
    if (!c) return;
    setMuted((m) => { if (m) c.unmute(); else c.mute(); return !m; });
  }, []);
  const sendDigit = useCallback((d: string) => { try { sdk.current?.client.sendDtmf(d); } catch { /* not in a call */ } }, []);
  const dismiss = useCallback(() => {
    setCall(null);
    setIncoming(null);
    setProblem(null);
    setSeconds(0);
    if (phaseRef.current === "ended") setPhaseBoth("ready");
    if (wrapEndsAtRef.current) readyNow();
  }, [setPhaseBoth, readyNow]);

  // The ringtone: plays while a call is ringing here (the sound is chosen by a Super Admin; each device can switch it off or turn it down)
  const stopRing = useCallback(() => { const a = audio.current; if (a) { a.pause(); a.currentTime = 0; } }, []);
  const playRing = useCallback((url: string) => {
    try {
      const a = audio.current && audio.current.src.endsWith(url) ? audio.current : new Audio(url);
      a.loop = true;
      a.volume = Math.min(1, (ring.adminVolume / 100) * (ringVolume / 100));
      audio.current = a;
      void a.play().catch(() => undefined);
    } catch { /* no audio available */ }
  }, [ring.adminVolume, ringVolume]);
  useEffect(() => {
    if (phase === "incoming" && ringOn) playRing(ring.url);
    else stopRing();
    return stopRing;
  }, [phase, ringOn, ring.url, playRing, stopRing]);

  // Not missing a call: the tab title flashes and, when the CRM is in another tab, a desktop notification appears
  useEffect(() => {
    if (phase !== "incoming" || !incoming) return;
    const original = document.title;
    const label = incoming.label || incoming.from_display;
    let on = true;
    const t = setInterval(() => { document.title = on ? `(1) Incoming · ${label}` : original; on = !on; }, 900);
    document.title = `(1) Incoming · ${label}`;
    let note: Notification | null = null;
    if (ring.desktopNotify && document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        note = new Notification(`Incoming call from ${label}`, { body: [incoming.sub, incoming.why === "OWNER" ? "You own this lead" : incoming.why === "TEAM" ? "Team call" : ""].filter(Boolean).join(" · "), tag: "incoming-call", requireInteraction: true });
        note.onclick = () => { window.focus(); note?.close(); };
      } catch { /* notifications blocked */ }
    }
    return () => { clearInterval(t); document.title = original; note?.close(); };
  }, [phase, incoming, ring.desktopNotify]);

  // Permission for desktop notifications is asked once, with an explanation, by the banner in the app frame (PushPrompt)

  // Keep the badge's missed-callback count fresh
  useEffect(() => {
    if (!allowed || phase === "off") return;
    const t = setInterval(() => { void refreshPending(); }, phase === "live" ? 15000 : 60000);
    return () => clearInterval(t);
  }, [allowed, phase === "off", phase === "live", refreshPending]);

  const updateCall = useCallback((patch: Partial<CallView>) => setCall((c) => (c ? { ...c, ...patch } : c)), []);
  const setRingtoneOn = useCallback((on: boolean) => { setRingOn(on); store.set("softphone-ring", on ? "on" : "off"); }, []);
  const setRingtoneVolume = useCallback((v: number) => { const x = Math.min(100, Math.max(0, Math.round(v))); setRingVolumeState(x); store.set("softphone-ring-volume", String(x)); }, []);
  const testRingtone = useCallback(() => {
    try {
      const a = new Audio(ring.url);
      a.volume = Math.min(1, (ring.adminVolume / 100) * (ringVolume / 100));
      void a.play().catch(() => undefined);
      setTimeout(() => { a.pause(); }, 3500);
    } catch { /* no audio available */ }
  }, [ring.url, ring.adminVolume, ringVolume]);

  const value: Softphone = {
    phase, offReason, problem, call, muted, seconds, recording, hasMobile, pending, refreshPending, incoming, answer, decline,
    rep, setAway, wrapLeft, readyNow, liveTick, callsTick, waiting,
    missedCallbacks, updateCall, setRingtoneOn, setRingtoneVolume, testRingtone,
    ringtone: { url: ring.url, adminVolume: ring.adminVolume, on: ringOn, volume: ringVolume },
    canTestCall: phase === "ready" && hasMobile,
    startTestCall, startCall, saveMobile, hangup, toggleMute, sendDigit, dismiss,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
